import React, { useState } from "react";
import {
  ActionIcon,
  Avatar,
  Box,
  Group,
  Indicator,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconMenu2 } from "@tabler/icons-react";
import { useNotifications } from "../../context/NotificationContext";
import { DESIGN_ICONS } from "../../theme/designIcons";
import { AlertsSheet } from "../notifications/AlertsSheet";
import { useValueChurnProbe } from "../../utils/loopDebug";
import { MobileNavDrawer } from "./MobileNavDrawer";
import { NavRail } from "./NavRail";
import { useNavModel, type NavModel } from "./useNavModel";
import "./NavBar.css";
import styles from "./NavBar.module.css";

interface NavBarProps {
  /** Onboarding wizard: logo and utilities only, no section buttons. */
  hideNavLinks?: boolean;
  /** Accepted for AppLayout compatibility; unused. */
  onboardingStage?: unknown;
}

/** The top bar's bell: unread count, opens the Alerts sheet. */
const AlertsBellButton: React.FC = () => {
  const { unacknowledgedCount } = useNotifications();
  const [opened, setOpened] = useState(false);
  const label =
    unacknowledgedCount > 0
      ? `Alerts, ${unacknowledgedCount} unread`
      : "Alerts";
  return (
    <>
      <Indicator
        disabled={unacknowledgedCount === 0}
        label={unacknowledgedCount > 99 ? "99+" : unacknowledgedCount}
        color="red"
        size={18}
        offset={4}
      >
        <ActionIcon
          variant="subtle"
          size="lg"
          className={styles.menuButton}
          aria-label={label}
          onClick={() => setOpened(true)}
        >
          <DESIGN_ICONS.Bell size={22} />
        </ActionIcon>
      </Indicator>
      <AlertsSheet opened={opened} onClose={() => setOpened(false)} />
    </>
  );
};

/** Mobile: a teal top bar whose hamburger opens the shared nav sheet. */
const MobileTopBar: React.FC<{ nav: NavModel }> = ({ nav }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <Box component="header" className={styles.topBar}>
        <ActionIcon
          onClick={() => setMenuOpen(true)}
          className={styles.menuButton}
          aria-label="Open navigation"
        >
          <IconMenu2 size={30} />
        </ActionIcon>
        <Text span className={styles.salonName}>
          {nav.account?.salonName}
        </Text>
        <Group gap="xs">
          <AlertsBellButton />
          <Avatar radius="xl" variant="white" color="teal" aria-hidden="true">
            {nav.account?.initials || "U"}
          </Avatar>
        </Group>
      </Box>
      <MobileNavDrawer opened={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

function NavBar({ hideNavLinks = false }: NavBarProps) {
  const nav = useNavModel();
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  // [LOOP] churn probe — NavBar is always mounted and bottomed the
  // max-update-depth componentStack; this shows which upstream identity is
  // re-rendering it.
  useValueChurnProbe("NavBar", {
    sections: nav.sections,
    availableLocations: nav.availableLocations,
  });

  return isMobile ? (
    <MobileTopBar nav={nav} />
  ) : (
    <NavRail nav={nav} hideNavLinks={hideNavLinks} />
  );
}

export default NavBar;
